import type { AlertType } from '@flash-global66/g-dialog-alert';
import { serializeForFirebase } from '../utils/remoteConfig.util';

/**
 * @description Valores por defecto para Remote Config
 */
export const REMOTE_CONFIG_TYPED_DEFAULTS = {
  /** Feature flag para requerir documento de accionistas en el proceso de onboarding */
  feature_shareholders_document_required: false as boolean,
  /** Feature flag para habilitar la funcionalidad de multi-envío web */
  feature_multi_send: false as boolean,
  /** Feature flag para habilitar la funcionalidad de remuneración CLP */
  feature_remuneration_clp: false as boolean,
  /** Feature flag para habilitar la xls de multienvio */
  feature_multitransaction_xls: false as boolean,
  /** Feature flag para habilitar el flujo de digitalización de los documentos de Composición Accionaria en el onboarding de Colombia */
  feature_shareholders_document_digitalization: false as boolean,
  /** Feature flag para habilitar el modal de actualización de documentos de accionistas */
  feature_shareholder_modal_mandatory: false as boolean,
  /** Feature flag para habilitar el bloqueo del usuario en el home por no actualizar documentos de accionistas */
  feature_shareholder_modal_user_lock: false as boolean,
  /** Feature flag para habilitar el modal de actualización de beneficiarios finales en Colombia */
  feature_shareholder_modal_mandatory_colombia: { enabled: false },
  /** Feature flag para habilitar el bloqueo de navegación en Colombia por beneficiarios finales pendientes */
  feature_shareholder_modal_user_lock_colombia: { enabled: false },
  /** Feature flag para mostrar el modal de beneficiarios finales al cohorte en revisión de compliance en Colombia (UNDER_COMPLIANCE_REVIEW / REVISION). Periodo de gracia con fecha límite propia. */
  feature_shareholder_modal_mandatory_colombia_review: { enabled: false },
  /** Feature flag para el hard-lock del cohorte en revisión de compliance en Colombia. Se enciende manualmente al vencer la fecha de gracia (23-jul-2026), sin deploy. */
  feature_shareholder_modal_user_lock_colombia_review: { enabled: false },
  /** Feature flag para habilitar la funcionalidad de webhooks */
  feature_webhooks: false as boolean,
  /** Feature flag para habilitar la funcionalidad de Credenciales API */
  feature_credential_api: { enabled: false },
  /** Feature flag del módulo Control de gastos (Expense Management): submenú del menú lateral
   * y rutas `/expense-management` (vía `meta.flag`). Usa `getFeatureFlag`, así que se puede
   * abrir por empresa o país con `segmentation`.
   * @example { "enabled": false, "segmentation": { "company_id": ["1234"] } }
   */
  feature_expense_management: { enabled: false },
  /** Feature flag para que el invitado nivel 3/4 que no es RL suba la carta de autorización antes de la validación de identidad. Apagado: carta después de Jumio */
  feature_authorization_before_identity: { enabled: false },
  /** Aviso de plazo para regularizar la identidad (Jumio). Extender el plazo = agregar una fecha
   * a `deadlines` (`YYYY-MM-DD`, ascendente); la vigente es la próxima que no haya pasado. La
   * primera fecha es el primer día sin plazo («antes del»); las siguientes son el último día con
   * plazo («hasta el»): para plazo hasta el 30 se publica el 30, no el 1 del mes siguiente.
   * `urgentDays`: desde cuántos días restantes el aviso pasa a rojo. `extendedNoticeDays`: cuántos
   * días, desde la fecha que pasó, se muestra «plazo extendido» con la fecha nueva. `enabled: false` lo apaga.
   * @example { "enabled": true, "deadlines": ["2026-11-01", "2026-11-30"], "urgentDays": 10, "extendedNoticeDays": 20 }
   */
  feature_jumio_regularization_deadline: {
    enabled: true,
    deadlines: ['2026-11-01', '2026-11-30'] as string[],
    urgentDays: 10,
    extendedNoticeDays: 20,
  },
  /** Feature flag para habilitar el flujo de referidos B2B (vistas y link en menú) */
  feature_referidos_b2b: false as boolean,
  /** Feature flag para habilitar pago local general en wallets */
  feature_localpayment_general: false as boolean,
  /** Feature flag para habilitar pago local en wallets (excepto México) */
  feature_localpayment_no_mx: false as boolean,
  /** Feature flag para habilitar pago local (payment) en cash-in */
  feature_localpayment_payment: false as boolean,
  /** Feature flag para habilitar P2P en Ecuador */
  feature_p2p_ecuador: false as boolean,
  /** Feature flag para habilitar auto-creación de wallets P2P en el flujo de multienvío XLS */
  feature_p2p_wallet_autocreate: { enabled: false },
  /** Feature flag para habilitar funcionalidad de Comex (solo Colombia) */
  feature_comex: false as boolean,
  /** Feature flag para habilitar el flujo de envío a Stablecoins. Soporta whitelist de clientes.
   * - enabled: true → habilitado para TODOS (whitelist se ignora)
   * - enabled: false + whitelist vacía → deshabilitado para todos
   * - enabled: false + whitelist con IDs → habilitado solo para esos clientes
   */
  feature_payout_stablecoins: {
    enabled: false,
    whitelist: [] as string[],
  },
  /** Feature flag para habilitar tarjetas por país offshore */
  feature_card_availability: {
    ar: { enabled: false },
    mx: { enabled: false },
    pe: { enabled: false },
    es: { enabled: false },
    default: { enabled: false },
  },
  /** Feature flag para usar el endpoint refactorizado de tarjetas (card/b2b/find-by-account); si está en false se usa card/b2b (legacy). */
  feature_smart_card_home_pomelo: true as boolean,
  /** Modo mantenimiento con mensaje personalizado */
  feature_maintenance_mode: {
    isActive: false,
    message: '',
    estimatedTime: '',
    reason: '',
  },
  /** Feature flag para mostrar alerta informativa de servicio al iniciar sesión por país */
  feature_service_alert: {
    all: {
      enabled: false,
      title: 'Aviso importante',
      description:
        'Estamos de vuelta, pero tus envíos podrían tardar un poco más de lo normal. Estamos trabajando para normalizar los tiempos.',
      type: 'info' as AlertType,
      buttonText: 'Entiendo',
    },
  },
  /** Feature flag para habilitar el módulo de RFQ */
  feature_rfq: false as boolean,
  /** Feature flag para habilitar el flujo de RFQ extendido */
  feature_rfq_extended: { enabled: false },
  /** Feature flag para habilitar la vista de lista de tarjetas en el dashboard */
  feature_multiple_cards: true as boolean,
  /** Feature flag para habilitar el flujo de Link de Cobro (InSwitch).
   * Usa `getFeatureFlag` — la segmentación (company_id, country, etc.) se resuelve
   * automáticamente contra el contexto del usuario.
   */
  feature_inswitch_payment_links: {
    enabled: false,
  },
  /** Feature flag para habilitar el flujo de Carga con Tarjeta InSwitch en Cash-In.
   * Usa `getFeatureFlag` — la segmentación (company_id, country, etc.) se resuelve
   * automáticamente contra el contexto del usuario.
   */
  feature_inswitch_cashin: {
    enabled: false,
  },
  /** Feature flag para controlar la asignación de usuarios externos en el flujo de creación de multi tarjeta */
  feature_tokenized_card: {
    enabled: false,
  },
  /** Feature flag para habilitar el nuevo flujo de Stablecoins en Wallet.
   * Usa `getFeatureFlag` — la segmentación (company_id, etc.) se resuelve automáticamente.
   */
  feature_wallet_stablecoins: {
    enabled: false,
  },
  /** Whitelist de empresas INSTITUCIONALES habilitadas para crear wallet stablecoin.
   * Solo aplica cuando company.institutional === true (control de UI, no de seguridad).
   * - enabled: true → todos los institucionales (whitelist ignorada)
   * - enabled: false + whitelist vacía → ningún institucional
   * - enabled: false + whitelist con IDs → habilitado solo para esos companyId
   */
  feature_wallet_stablecoins_institutional: {
    enabled: false,
    whitelist: [] as string[],
  },
  /** Feature flag para mostrar el badge de rendimientos en wallets stablecoin (USDT/USDC).
   * Usa `getFeatureFlag` — la segmentación (company_id, etc.) se resuelve automáticamente,
   * así que el piloto por cliente se configura desde Remote Config sin tocar código.
   *
   * Es independiente de `feature_wallet_stablecoins`: gobierna solo el badge, no el resto
   * de la UI stablecoin. Aun con este flag encendido el badge no se renderiza mientras
   * `YIELD_BADGE_COPY.USDT/USDC.earning` esté vacío (ver walletYield.constant.ts).
   */
  feature_wallet_stablecoins_yield: {
    enabled: false,
  },
  /** Wallet Stablecoins v3: habilita BTC y ETH con un solo flag, porque se activan juntos.
   * Usa `getFeatureFlag` — se abre por empresa (`company_id`) o por país (`country`) con
   * `segmentation`. Es independiente de los flags de USDC/USDT.
   * @example { "enabled": false, "segmentation": { "company_id": ["1234"], "country": ["CL"] } }
   */
  feature_wallet_stablecoins_v3: {
    enabled: false,
  },
  /** Feature flag para habilitar el módulo completo de Bre-B (método de pago, envíos y depósitos).
   * Controla la visibilidad de todo el módulo Bre-B en la plataforma B2B:
   * entrypoints en menú, flujos de pago, envío y depósito asociados.
   */
  feature_breb_b2b: {
    enabled: false,
  },
  /** Aviso de caida del proveedor Bre-B (Transfiya). Eje inverso de
   * `feature_breb_b2b`: aquella decide si Bre-B EXISTE, esta decide si OPERA.
   * Encendida, la superficie sigue visible y cada entrada abre el aviso de
   * fuera de servicio en lugar de ejecutar. Admite `segmentation` porque toda
   * su superficie es post-login, con el KYC ya cargado.
   * @example { "enabled": false, "segmentation": { "country": ["CO"] } }
   */
  feature_breb_maintenance: {
    enabled: false,
  },
  /** Feature flag para forzar reCAPTCHA V2 solo en login (email + password) y registro.
   * - enabled: false → V3 primero y fallback a V2 si el backend responde RECAPTCHA_SCORE_TOO_LOW (comportamiento actual).
   * - enabled: true → omite V3 y usa siempre el widget V2 en esos dos flujos.
   */
  feature_recaptcha_v2_only: {
    enabled: false,
  },
  /** Feature flag para habilitar la experiencia completa de Cuentas Virtuales B2B (ACH / CCA).
   * Actúa como kill-switch: controla la visibilidad de todo el módulo desde configuración,
   * sin requerir un nuevo despliegue. Cubre entrypoints en menú, vistas y flujos asociados.
   */
  feature_virtual_accounts_b2b: {
    enabled: false,
  },
  /** Feature flag para habilitar el paso de credenciales SII (Chile) en el
   * onboarding B2B autogestionado. Se combina con la validación isChile en
   * el flujo general: el step solo se muestra si el usuario es chileno Y este
   * flag está en true. Si el flag se apaga con un usuario ya parado en el
   * step, el fallback del onboarding lo mueve a COMPANY_DATA para no
   * bloquearlo.
   */
  feature_onboarding_sii_credentials: {
    enabled: false,
  },
  /** Feature flag para habilitar el drawer de credenciales SII (Chile) desde
   * el perfil. Requiere que el usuario sea chileno, que ambos KYCs (b2bUser y
   * company) estén en APPROVED, y que el usuario no haya conectado
   * credenciales en la sesión actual. Solo controla la visibilidad del banner
   * disparador y del drawer en Profile; no afecta al onboarding.
   */
  feature_profile_sii_credentials: {
    enabled: false,
  },
  /** Feature flag para habilitar las tabs "Cuentas principales" / "Cuentas adicionales"
   * en el slot de wallets del Home. Kill-switch: en `false` el Home renderiza
   * `HomeWalletsSection` sin cambios; en `true` renderiza `HomeAccountTabsSection`
   * en ese mismo slot. El resto de las secciones de Home (novedades, movimientos
   * recientes, TRM, modals) no dependen de este flag.
   */
  feature_multi_accounts_b2b: {
    enabled: false,
  },
  /** Feature flag para habilitar los envíos programados (scheduled transfers).
   * Kill-switch del feature completo: opción "Programar" en el selector de tipo
   * de envío, link "Envíos programados" del menú lateral y rutas
   * `/scheduled-transfers` (lista y detalle, vía `meta.flag`).
   * Reemplaza al toggle de código `SCHEDULED_TRANSFER_ENTRY_ENABLED`, que
   * obligaba a un deploy para apagar el feature.
   */
  feature_scheduled_transfers: {
    enabled: false,
  },
  /** Feature flag para habilitar los flujos vinculados a las tarjetas temporales.
   */
  feature_temp_cards: {
    enabled: false,
  },
  /** Feature flag para habilitar la Smart Card Off Shore ("Virtual en dólares")
   * en cuentas chilenas. Kill-switch del landing y del paso previo del
   * drawer "Solicitar Smart Card"; el backend y `isOffshoreCountry` no se
   * modifican. Default OFF: se activa por segmentación desde Remote Config.
   */
  feature_offshore_smartcard_chl: {
    enabled: false,
  },
  /** Feature flag para mostrar el selector de redes stablecoin en el cotizador
   * (`TransactionQuoter`) con el costo por red del EP `route/b2b/destiny-banks/cost`.
   * Kill-switch aislado: apagado, el cotizador se comporta exactamente igual
   * que antes del feature. Independiente de `feature_wallet_stablecoins`.
   */
  feature_quoter_stablecoin_networks: {
    enabled: false,
  },
  /** Feature flag para ocultar el costo por red del selector del cotizador
   * (`TransactionQuoter`) cuando el envío sale de una **moneda fiat** (BSI-150).
   * Kill-switch aislado: apagado, el costo vuelve a mostrarse desde cualquier
   * origen. No gobierna la exclusión de `CURRENCIES_WITHOUT_NETWORK_COST`, que
   * es una regla de producto y aplica con la flag apagada también. Depende de
   * `feature_quoter_stablecoin_networks`: sin selector no hay costo que ocultar.
   */
  feature_quoter_stablecoin_hide_network_cost: {
    enabled: false,
  },
  /** Feature flag para habilitar la dispersión Bre-B en empresas no SEDPE.
   * Kill-switch: apagado, Bre-B se comporta exactamente igual que hoy;
   * encendido, habilita el flujo de dispersión para esas empresas.
   * @example
   * useBrebDispersionB2b().isEnabled
   */
  feature_breb_dispersion_b2b: {
    enabled: false,
  },
  /** Feature flag para habilitar la feature de multi-aprobaciones.
   * Controla el acceso a la funcionalidad a través de ocultar los checkboxes de
   * la tabla y el componente de redirección al step de recotización y resumen.
   */
  feature_multi_approvals: {
    enabled: false,
  },
  /** Feature flag para habilitar el login por passkey (WebAuthn) en la pantalla
   * de inicio de sesion. Kill-switch del hero y de la ceremonia de
   * autenticacion; apagada, `/auth/log-in` se comporta exactamente igual que
   * antes del feature.
   *
   * NO declarar `segmentation` aca ni en el valor JSON de la consola: la flag
   * se lee sin sesion, `resolveEnabled` corta a `false` cuando hay
   * segmentation y el contexto del usuario no esta listo, y el hero quedaria
   * apagado en produccion sin error ni log. El rollout gradual se hace por
   * condicion de porcentaje en la consola, no por segmentation.
   */
  feature_webauthn_login: {
    enabled: false,
  },
  /** Feature flag para habilitar el registro y la gestion de passkeys: la
   * seccion de Perfil > Seguridad y la invitacion posterior al login con
   * contrasena. Se lee siempre con sesion activa, asi que admite
   * `segmentation` por `user_id` o `email` para pilotear con usuarios internos.
   */
  feature_webauthn_registration: {
    enabled: false,
  },
  /** Feature flag para exigir una edicion real en la pantalla de Representante
   * Legal del onboarding autogestionado. Gatea DOS botones:
   * - el Guardar de cada representante, deshabilitado mientras su formulario
   *   sea igual al estado con el que se abrio (en el alta de uno nuevo no
   *   bloquea, porque no hay estado previo contra el cual comparar);
   * - el Continuar del paso, deshabilitado hasta que se haya guardado un
   *   representante con cambios.
   *
   * Gatea solo el modo de varios representantes (administracion conjunta y
   * revision de compliance), en los flujos general y Colombia; el modo de un
   * solo representante no cambia. Cubre el caso real: compliance rechaza y
   * manda a actualizar informacion; el motivo puede ser cualquier campo, no
   * solo el documento. Kill-switch de un experimento de friccion.
   *
   * NO declarar `segmentation` aca: el gating por KYC fuerza `enabled: false`
   * mientras el KYC no respondio, y esta pantalla ES el onboarding, asi que
   * el gate se encenderia o no segun el timing de esa respuesta, sin error
   * ni log. Ver el mismo caso documentado en `feature_webauthn_login`.
   */
  feature_onboarding_rl_edit_required: {
    enabled: false,
  },
  /** Feature flag para habilitar el radio de variante de destino en el
   * cotizador de remesas (`TransactionQuoter`). Kill-switch: apagado, el cotizador
   * queda igual que en release; encendido, si un destino internacional tiene
   * más de una ruta (tipo de envío, o moneda local vs. Dólares) pide elegirla
   * antes de cotizar, con aviso SEPA en euros.
   */
  feature_quoter_currency_selector: {
    enabled: false,
  },
} as const;

/** Defaults serialized for Firebase Remote Config */
export const REMOTE_CONFIG_DEFAULTS = serializeForFirebase(
  REMOTE_CONFIG_TYPED_DEFAULTS
);

/**
 * @description Intervalos de fetch para Remote Config */
export const REMOTE_CONFIG_INTERVALS = {
  DEVELOPMENT: 0,
  PRODUCTION: 3600000, // 1 hora en milisegundos
};

/**
 * @description Timeout para operaciones de fetch de Remote Config
 */
export const REMOTE_CONFIG_TIMEOUT = 60000; // 60 segundos

/**
 * @description Máximo de reintentos para la conexión realtime de Remote Config
 */
export const MAX_RETRIES = 3;

/**
 * @description Base de delay para la conexión realtime de Remote Config
 */
export const BASE_DELAY_MS = 1000;

/**
 * @description Operador por defecto cuando un FeatureFlag declara `segmentation`
 * sin especificar `operator`. `OR` significa que basta con que alguna propiedad
 * definida matchee el contexto del usuario.
 */
export const DEFAULT_SEGMENTATION_OPERATOR = 'OR';
